import { Shield, Calendar, Zap } from 'lucide-react'

interface StreakDisplayProps {
  studyStreak: number
  longestStreak: number
  assignmentStreak: number
  shields: number
}

export default function StreakDisplay({
  studyStreak,
  longestStreak,
  assignmentStreak,
  shields,
}: StreakDisplayProps) {
  // Calculate next milestone
  const milestones = [3, 7, 14, 30, 60, 90]
  const nextMilestone = milestones.find((m) => m > studyStreak) || 90
  const daysToMilestone = nextMilestone - studyStreak

  return (
    <div className="space-y-4">
      {/* Main streak display */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`text-4xl ${studyStreak > 0 ? 'animate-fire' : ''}`}>
            {studyStreak > 0 ? '🔥' : '❄️'}
          </div>
          <div>
            <p className="text-3xl font-bold">
              {studyStreak} {studyStreak === 1 ? 'Day' : 'Days'}
            </p>
            <p className="text-sm text-gray-400">Current Streak</p>
          </div>
        </div>

        {/* Shields */}
        <div className="flex items-center gap-2 px-4 py-2 bg-gray-700 rounded-lg">
          <Shield className="w-5 h-5 text-blue-400" />
          <span className="font-medium">{shields}</span>
          <span className="text-sm text-gray-400">shields</span>
        </div>
      </div>

      {/* Next milestone */}
      {studyStreak > 0 && (
        <div className="p-3 bg-gray-700/50 rounded-lg">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-400">Next milestone</span>
            <span className="text-sm font-medium text-quest-accent">
              {nextMilestone} days
            </span>
          </div>
          <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-orange-500 to-red-500"
              style={{
                width: `${Math.min(100, ((studyStreak % nextMilestone) / nextMilestone) * 100)}%`,
              }}
            />
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {daysToMilestone} day{daysToMilestone !== 1 ? 's' : ''} to go!
          </p>
        </div>
      )}

      {/* Stats row */}
      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-700">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-gray-500" />
          <div>
            <p className="text-sm font-medium">{longestStreak} days</p>
            <p className="text-xs text-gray-500">Personal best</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Zap className="w-4 h-4 text-gray-500" />
          <div>
            <p className="text-sm font-medium">{assignmentStreak} on-time</p>
            <p className="text-xs text-gray-500">Assignment streak</p>
          </div>
        </div>
      </div>
    </div>
  )
}
